import * as React from 'react';
import { renderToString } from 'react-dom/server';

import { ClassroomInterface, SkillCategoryInterface } from './interfaces';

import { ALL_PRACTICE, CLASS_PRACTICE, } from '../shared/shared'
import { useReportPremiumContext } from '../shared/report_premium_context';

import {
  Checkbox,
  DropdownInput,
  EXTRA_SMALL_ICON_BASE_SRC,
  SMALL_ICON_BASE_SRC,
  SegmentedControl,
  Tooltip,
} from '../../../../Shared/index';

const helpImgSrc = `${EXTRA_SMALL_ICON_BASE_SRC}/help.svg`;
const lockIconSrc = `${SMALL_ICON_BASE_SRC}/lock.svg`

const ALL_SKILL_SUB_CATEGORY_OPTION = { value: null, label: 'All sub-categories'}
const ALL_SKILL_CATEGORY_OPTION = { value: null, label: 'All categories'}
const ALL_STUDENTS_OPTION = { value: null, label: 'All students' }

interface SkillsReportSubheaderProps {
  byStudent: boolean;
  classroom: ClassroomInterface;
  filteredSkillCategories: SkillCategoryInterface[];
  filteredSkillSubCategories: SkillCategoryInterface[];
  includeELL: boolean;
  onSkillCategoryChange: (skillCategory: any) => void;
  onSkillSubCategoryChange?: (skillSubCategory: any) => void;
  onStudentChange?: (student: any) => void;
  practiceScope: string;
  selectedSkillCategoryId: any;
  selectedSkillSubCategoryId: any;
  selectedStudentId: any;
  setIncludeELL: (includeELL: boolean) => void;
  setPracticeScope: (practiceScope: string) => void;
}

const SkillsReportSubheader: React.FC<SkillsReportSubheaderProps> = ({
  byStudent,
  classroom,
  filteredSkillCategories,
  filteredSkillSubCategories,
  includeELL,
  onSkillCategoryChange,
  onSkillSubCategoryChange,
  onStudentChange,
  practiceScope,
  selectedSkillCategoryId,
  selectedSkillSubCategoryId,
  selectedStudentId,
  setIncludeELL,
  setPracticeScope,
}) => {
  const { hasSchoolOrDistrictPremium } = useReportPremiumContext();

  function handleClickPracticeScopeButton(e: React.MouseEvent<HTMLButtonElement>) {
    setPracticeScope(e.currentTarget.value)
  }

  function toggleIncludeELL() {
    if (setIncludeELL && includeELL !== undefined) {
      setIncludeELL(!includeELL);
    }
  }

  const skillCategoryOptions = [
    byStudent? null : ALL_SKILL_CATEGORY_OPTION,
    ...filteredSkillCategories
      .map(skillCategory => ({ label: skillCategory.name, value: skillCategory.id }))
  ].filter(Boolean)

  const skillCategoryDropdown = (
    <DropdownInput
      className="small bordered"
      handleChange={onSkillCategoryChange}
      id="skill-category-dropdown"
      isSearchable={true}
      label='Skill Category'
      options={skillCategoryOptions}
      value={skillCategoryOptions.find(selectedSkillCategory => selectedSkillCategory.value === selectedSkillCategoryId)}
    />
  )

  const skillSubCategoryOptions = [
    ALL_SKILL_SUB_CATEGORY_OPTION,
    ...filteredSkillSubCategories
      .filter(skillSubCategory => skillSubCategory.parent_id === selectedSkillCategoryId)
      .map(skillSubCategory => ({ label: skillSubCategory.name, value: skillSubCategory.id }))
  ]

  const skillSubCategoryDropdown = onSkillSubCategoryChange ? (
    <DropdownInput
      className="small bordered"
      handleChange={onSkillSubCategoryChange}
      id="skill-sub-category-dropdown"
      isSearchable={true}
      label='Skill Sub-Category'
      options={skillSubCategoryOptions}
      value={skillSubCategoryOptions.find(selectedSkillSubCategory => selectedSkillSubCategory.value === selectedSkillSubCategoryId)}
    />
  ) : null

  const studentOptions = [ALL_STUDENTS_OPTION, ...(classroom?.students?.map(student => ({ label: student.name, value: student.id })) || [])]

  const studentDropdown = onStudentChange ? (
    <DropdownInput
      className="small bordered"
      handleChange={onStudentChange}
      id="student-dropdown"
      isSearchable={true}
      label='Student'
      options={studentOptions}
      value={studentOptions.find(selectedStudent => selectedStudent.value === selectedStudentId)}
    />
  ) : null

  return (
    <div className="subheader">
      <SegmentedControl
        activeTab={practiceScope}
        buttons={[
          {
            label: CLASS_PRACTICE,
            onClick: handleClickPracticeScopeButton,
          },
          {
            label: ALL_PRACTICE,
            labelIcon: hasSchoolOrDistrictPremium ? null : <img alt="Lock" src={lockIconSrc} />,
            onClick: handleClickPracticeScopeButton,
          },
        ]}
        label="Practice Scope"
        labelAdjacentElement={(
          <Tooltip
            tooltipText={renderToString(<React.Fragment>Choose <strong>'All Practice'</strong> to see your students' complete history, including from other teachers and previous years. Select <strong>'Class Practice'</strong> to focus only on their work in the selected class.</React.Fragment>)}
            tooltipTriggerText={<img alt="Help icon" src={helpImgSrc} />}
          />
        )}
        size="small"
      />
      <div className="filters">
        <Checkbox
          id="include-ell-skills"
          label="Switch to ELL skills"
          onClick={toggleIncludeELL}
          selected={includeELL}
        />
        {skillCategoryDropdown}
        {byStudent ? skillSubCategoryDropdown : studentDropdown}
      </div>
    </div>
  );
};

export default SkillsReportSubheader;
